import { useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, Stack, Button, Popover, Link, Divider, Alert, AlertTitle } from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EventIcon from '@mui/icons-material/Event';
import PlaceIcon from '@mui/icons-material/Place';
import GroupIcon from '@mui/icons-material/Group';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAllProjects, useReifegradOverview } from '../../api/hooks/useProjects';
import { useGlobalMeetings, useDeleteGlobalMeeting, type GlobalMeeting } from '../../api/hooks/useGlobalMeetings';
import { useNotifications } from '../../context/NotificationContext';
import { RequirePermission } from '../../components/common/RequirePermission';
import { PROJECT_PERMISSIONS } from '@pm/shared';
import { ApiError } from '../../api/client';
import { PmInfoBox } from '../../components/pm';
import { ProjectTimelineGantt, type TimelineProject, type TimelineMeeting } from './ProjectTimelineGantt';
import { GlobalMeetingDialog } from './GlobalMeetingDialog';
import { GlobalMeetingsList } from './GlobalMeetingsList';
import { computeAmpelBuckets } from './ampel';
import { ProjectFilterBar, matchesProjectFilter, EMPTY_PROJECT_FILTER, type ProjectFilter } from './ProjectFilterBar';
import { ProjectsTable, type ProjectRow } from '../projects/components/ProjectsTable';

interface RawProject {
  id: string;
  name: string;
  nickname?: string | null;
  planned_start?: string | null;
  planned_end?: string | null;
  state: string;
  project_no?: string | null;
  priority?: string;
  bill_type?: string | null;
  deadline?: string | null;
  /** Projektleiter (Verantwortlicher) — nur vorhanden bei ?include=lead. */
  lead?: { coworker_id: string | null; name: string | null } | null;
}

// Abgeschlossene/abgebrochene Projekte erscheinen nicht auf der Timeline.
const HIDDEN_STATES = new Set(['COMPLETED', 'CANCELED']);

/**
 * Timeline — Landing-Page nach Login. Projektübergreifendes Gantt über alle
 * Projekte mit gesetztem Start- und Enddatum, plus globale (tenant-weite)
 * Meetings als Marker. Klick auf einen Balken öffnet das Projekt-Dashboard,
 * Klick auf ein Meeting-Icon öffnet ein Detail-Popover.
 */
export function TimelinePage() {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const { notify } = useNotifications();
  // include=lead: liefert pro Projekt lead:{coworker_id,name} (batched, kein N+1)
  // für die Projektleiter-Spalte/-Filter in der Tabelle. Additiv im Backend.
  const { data, isLoading, error } = useAllProjects({ include: 'lead' });
  const { data: reifegradResp } = useReifegradOverview();
  const { data: meetingsResp } = useGlobalMeetings();
  const deleteMeeting = useDeleteGlobalMeeting();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMeeting, setEditMeeting] = useState<GlobalMeeting | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selected, setSelected] = useState<GlobalMeeting | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Gemeinsamer Filter für Timeline-Balken UND Projekttabelle.
  const [filter, setFilter] = useState<ProjectFilter>(EMPTY_PROJECT_FILTER);

  const all = useMemo(() => {
    const resp = data as unknown as { data?: RawProject[] } | undefined;
    return resp?.data ?? [];
  }, [data]);

  // Gemeinsamer Filter clientseitig auf den gesamten Projektbestand.
  const filtered = useMemo(
    () => all.filter((p) => matchesProjectFilter({ name: p.name, state: p.state, priority: p.priority ?? '' }, filter)),
    [all, filter],
  );

  // Tabelle: alle gefilterten Projekte (auch fertige/abgebrochene).
  const tableRows: ProjectRow[] = useMemo(
    () => filtered.map((p) => ({
      id: p.id, project_no: p.project_no ?? null, name: p.name, state: p.state,
      priority: p.priority ?? 'NORMAL', bill_type: p.bill_type ?? null, deadline: p.deadline ?? null,
      lead_id: p.lead?.coworker_id ?? null, lead_name: p.lead?.name ?? null,
    })),
    [filtered],
  );

  const meetings = useMemo(() => {
    const resp = meetingsResp as unknown as { data?: GlobalMeeting[] } | undefined;
    return resp?.data ?? [];
  }, [meetingsResp]);

  // Reifegrad-Schritte je Projekt (Bulk-Overview) -> Map fuer die Ampel-Summe.
  const stepsById = useMemo(() => {
    const resp = reifegradResp as unknown as { data?: Array<{ project_id: string; steps: number[] }> } | undefined;
    const map = new Map<string, number[]>();
    (resp?.data ?? []).forEach((r) => map.set(r.project_id, r.steps ?? []));
    return map;
  }, [reifegradResp]);

  // Abgeschlossene/abgebrochene Projekte werden auf der Timeline nicht angezeigt
  // (auf Basis des gemeinsamen Filters).
  const active = useMemo(() => filtered.filter((p) => !HIDDEN_STATES.has(p.state)), [filtered]);

  // Nur aktive Projekte mit gesetztem Start UND Ende erscheinen auf der Timeline.
  const projects: TimelineProject[] = useMemo(
    () =>
      active
        .filter((p): p is RawProject & { planned_start: string; planned_end: string } =>
          !!p.planned_start && !!p.planned_end)
        .map((p) => {
          const steps = stepsById.get(p.id);
          return {
            id: p.id,
            name: p.name,
            nickname: p.nickname ?? null,
            planned_start: p.planned_start,
            planned_end: p.planned_end,
            state: p.state,
            buckets: steps && steps.length > 0 ? computeAmpelBuckets(steps) : undefined,
          };
        }),
    [active, stepsById],
  );

  const timelineMeetings: TimelineMeeting[] = useMemo(
    () => meetings.map((m) => ({ id: m.id, titel: m.titel, datum: m.datum, uhrzeit: m.uhrzeit })),
    [meetings],
  );

  // Nur aktive Projekte ohne Termine gelten als "ausgeblendet" (Hinweis); fertige/
  // abgebrochene werden bewusst nicht gezeigt und nicht mitgezaehlt.
  const skipped = active.length - projects.length;

  // 403 auf der Projektliste ist KEIN „leer", sondern eine Sperre: fehlt dem
  // Benutzer im aktuellen Mandanten das Projekt-Leserecht, muss das sichtbar
  // sein — sonst sieht das System kaputt aus statt gesperrt.
  const forbidden = error instanceof ApiError && error.status === 403;

  const openCreate = () => { setEditMeeting(null); setDialogOpen(true); };
  const closePopover = () => { setAnchorEl(null); setSelected(null); setConfirmDelete(false); };

  const handleMeetingSelect = (m: TimelineMeeting, anchor: HTMLElement) => {
    const full = meetings.find((x) => x.id === m.id) ?? null;
    setSelected(full);
    setAnchorEl(anchor);
    setConfirmDelete(false);
  };

  const handleEdit = () => {
    if (!selected) return;
    setEditMeeting(selected);
    setDialogOpen(true);
    closePopover();
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await deleteMeeting.mutateAsync(selected.id);
      notify(tc('status.deleted', 'Gelöscht'), 'success');
      closePopover();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : tc('errors.generic', 'Fehler');
      notify(msg, 'error');
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
            <TimelineIcon color="primary" />
            <Typography variant="h5">{t('timeline.title', 'Programm HQ')}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t('timeline.subtitle', 'Projektübergreifende Gesamtübersicht aller Projekte mit Start- und Enddatum.')}
          </Typography>
        </Box>
        <RequirePermission permission={PROJECT_PERMISSIONS.GLOBAL_MEETING.CREATE}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ flexShrink: 0 }}>
            {t('globalMeeting.create', 'Neuer Termin')}
          </Button>
        </RequirePermission>
      </Stack>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : forbidden ? (
        <Alert severity="warning" data-testid="projects-forbidden">
          <AlertTitle>{t('timeline.forbiddenTitle', 'Kein Zugriff auf die Projekte dieses Mandanten')}</AlertTitle>
          {t(
            'timeline.forbiddenBody',
            'Ihrem Benutzer fehlt in diesem Mandanten die Berechtigung für Projekte. Bitte wenden Sie sich an Ihre Administration.',
          )}
        </Alert>
      ) : error ? (
        <PmInfoBox variant="error" title={t('timeline.errorTitle', 'Programm HQ konnte nicht geladen werden')}>
          {error.message}
        </PmInfoBox>
      ) : (
        <Stack spacing={4}>
          {/* Gemeinsamer Filter — wirkt auf Timeline-Balken UND Tabelle. */}
          <ProjectFilterBar value={filter} onChange={setFilter} />

          {/* Timeline-Gantt */}
          {projects.length === 0 ? (
            <PmInfoBox variant="info" title={t('timeline.emptyTitle', 'Keine Projekte auf der Timeline')}>
              {t(
                'timeline.emptyBody',
                'Es gibt aktuell keine Projekte mit gesetztem Start- und Enddatum. Lege ein Projekt mit geplantem Start und Ende an, damit es hier erscheint.',
              )}
            </PmInfoBox>
          ) : (
            <Stack spacing={2}>
              <ProjectTimelineGantt
                projects={projects}
                onSelect={(id) => navigate(`/projects/${id}/dashboard`)}
                meetings={timelineMeetings}
                onMeetingSelect={handleMeetingSelect}
              />
              {skipped > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {t('timeline.skippedHint', '{{count}} Projekt(e) ohne Start-/Enddatum werden nicht angezeigt.', { count: skipped })}
                </Typography>
              )}
            </Stack>
          )}

          {/* Projekttabelle unterhalb der Timeline (Menüpunkt „Projekte" entfällt). */}
          <ProjectsTable projects={tableRows} loading={isLoading} />

          {/* Termin-Liste */}
          <GlobalMeetingsList
            meetings={meetings}
            onEditDetail={(m) => { setEditMeeting(m); setDialogOpen(true); }}
          />
        </Stack>
      )}

      {/* Detail-Popover am Meeting-Icon */}
      <Popover
        open={!!anchorEl && !!selected}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {selected && (
          <Box sx={{ p: 2, maxWidth: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{selected.titel}</Typography>
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1} alignItems="center">
                <EventIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2">
                  {dayjs(selected.datum).format('dddd, DD.MM.YYYY')}{selected.uhrzeit ? `, ${selected.uhrzeit}` : ''}
                </Typography>
              </Stack>
              {selected.ort && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <PlaceIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2">{selected.ort}</Typography>
                </Stack>
              )}
              {selected.teilnehmer && (
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <GroupIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25 }} />
                  <Typography variant="body2">{selected.teilnehmer}</Typography>
                </Stack>
              )}
              {selected.beschreibung && (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selected.beschreibung}
                </Typography>
              )}
              {selected.link_uri && (
                <Link href={selected.link_uri} target="_blank" rel="noopener" variant="body2">
                  {selected.link_uri}
                </Link>
              )}
            </Stack>

            {confirmDelete ? (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('globalMeeting.deleteConfirm', 'Diesen Termin wirklich löschen?')}
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button size="small" onClick={() => setConfirmDelete(false)}>
                    {tc('actions.cancel', 'Abbrechen')}
                  </Button>
                  <Button size="small" color="error" variant="contained" onClick={handleDelete} disabled={deleteMeeting.isPending}>
                    {tc('actions.delete', 'Löschen')}
                  </Button>
                </Stack>
              </>
            ) : (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <RequirePermission permission={PROJECT_PERMISSIONS.GLOBAL_MEETING.UPDATE}>
                    <Button size="small" startIcon={<EditIcon />} onClick={handleEdit}>
                      {tc('actions.edit', 'Bearbeiten')}
                    </Button>
                  </RequirePermission>
                  <RequirePermission permission={PROJECT_PERMISSIONS.GLOBAL_MEETING.DELETE}>
                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)}>
                      {tc('actions.delete', 'Löschen')}
                    </Button>
                  </RequirePermission>
                </Stack>
              </>
            )}
          </Box>
        )}
      </Popover>

      <GlobalMeetingDialog
        open={dialogOpen}
        mode={editMeeting ? 'edit' : 'create'}
        initial={editMeeting}
        onClose={() => { setDialogOpen(false); setEditMeeting(null); }}
      />
    </Box>
  );
}
